import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  BanknotesIcon,
  EyeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  adminGetRefunds,
  adminApproveRefund,
  adminRejectRefund,
  adminCompleteRefund,
  RefundWithApplication,
  getRefundStatusLabel,
  getRefundStatusColor,
} from '../../services/refundService';

const RefundManagement: React.FC = () => {
  const [refunds, setRefunds] = useState<RefundWithApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'requested' | 'approved' | 'completed' | 'cancelled'>('all');

  // 모달 상태
  const [selectedRefund, setSelectedRefund] = useState<RefundWithApplication | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // 처리 중 상태
  const [processingId, setProcessingId] = useState<string | null>(null);

  useEffect(() => {
    loadRefunds();
  }, [statusFilter]);

  const loadRefunds = async () => {
    setLoading(true);
    try {
      const response = await adminGetRefunds(statusFilter === 'all' ? undefined : statusFilter);
      if (response.success && response.data) {
        setRefunds(response.data);
      } else {
        console.error('Failed to load refunds:', response.error);
      }
    } catch (error) {
      console.error('Error loading refunds:', error);
    } finally {
      setLoading(false);
    }
  };

  // 필터링된 환불 목록
  const filteredRefunds = refunds.filter((refund) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      refund.application?.applicant_name?.toLowerCase().includes(searchLower) ||
      refund.application?.application_number?.toLowerCase().includes(searchLower) ||
      refund.program_info?.name?.toLowerCase().includes(searchLower) ||
      refund.user_info?.phone?.includes(searchTerm)
    );
  });

  // 환불 승인
  const handleApprove = async (refundId: string) => {
    if (!window.confirm('환불을 승인하시겠습니까?')) return;

    setProcessingId(refundId);
    try {
      const response = await adminApproveRefund(refundId);
      if (response.success) {
        alert('환불이 승인되었습니다.');
        await loadRefunds();
      } else {
        alert(response.error?.message || '승인 처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('Approve error:', error);
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // 환불 거절 모달 열기
  const openRejectModal = (refund: RefundWithApplication) => {
    setSelectedRefund(refund);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  // 환불 거절 처리
  const handleReject = async () => {
    if (!selectedRefund) return;
    if (!rejectionReason.trim()) {
      alert('거절 사유를 입력해주세요.');
      return;
    }

    setProcessingId(selectedRefund.id);
    try {
      const response = await adminRejectRefund(selectedRefund.id, rejectionReason);
      if (response.success) {
        alert('환불이 거절되었습니다.');
        setShowRejectModal(false);
        await loadRefunds();
      } else {
        alert(response.error?.message || '거절 처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('Reject error:', error);
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // 환불 완료 처리
  const handleComplete = async (refundId: string) => {
    if (!window.confirm('환불 완료 처리를 하시겠습니까?\n\n수강 신청이 자동으로 취소되고 대기자가 승급될 수 있습니다.')) return;

    setProcessingId(refundId);
    try {
      const response = await adminCompleteRefund(refundId);
      if (response.success) {
        alert('환불이 완료 처리되었습니다.');
        await loadRefunds();
      } else {
        alert(response.error?.message || '완료 처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('Complete error:', error);
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // 상세 모달 열기
  const openDetailModal = (refund: RefundWithApplication) => {
    setSelectedRefund(refund);
    setShowDetailModal(true);
  };

  // 통계
  const stats = {
    requested: refunds.filter((r) => r.refund_status === 'requested').length,
    approved: refunds.filter((r) => r.refund_status === 'approved').length,
    completed: refunds.filter((r) => r.refund_status === 'completed').length,
    // 총 환불액은 completed 상태만 합산 (취소/거절 제외)
    total: refunds
      .filter((r) => r.refund_status === 'completed')
      .reduce((sum, r) => sum + (r.refund_amount || 0), 0),
  };

  // 상태 라벨 헬퍼 (사용자 취소 vs 관리자 거절 구분)
  const getDisplayStatusLabel = (refund: RefundWithApplication) => {
    if (refund.refund_status === 'cancelled') {
      // rejection_reason이 있으면 관리자 거절, 없으면 사용자 취소
      return refund.rejection_reason ? '환불 거절' : '사용자 취소';
    }
    return getRefundStatusLabel(refund.refund_status);
  };

  const getDisplayStatusColor = (refund: RefundWithApplication) => {
    if (refund.refund_status === 'cancelled') {
      return refund.rejection_reason
        ? 'bg-red-100 text-red-800'  // 관리자 거절
        : 'bg-gray-100 text-gray-600';  // 사용자 취소
    }
    return getRefundStatusColor(refund.refund_status);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* 헤더 */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800">환불 관리</h1>
          <p className="text-slate-600 mt-1">수강생 환불 신청을 관리합니다.</p>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">대기 중</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.requested}</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-full">
                <BanknotesIcon className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">처리 중</p>
                <p className="text-2xl font-bold text-blue-600">{stats.approved}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-full">
                <CheckCircleIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">완료</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-full">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">총 환불액</p>
                <p className="text-2xl font-bold text-slate-800">{stats.total.toLocaleString()}원</p>
              </div>
              <div className="p-3 bg-slate-100 rounded-full">
                <BanknotesIcon className="h-6 w-6 text-slate-600" />
              </div>
            </div>
          </div>
        </div>

        {/* 필터 및 검색 */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-slate-200">
          <div className="flex flex-col md:flex-row gap-4">
            {/* 검색 */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="이름, 신청번호, 프로그램명, 연락처 검색"
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* 상태 필터 */}
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-slate-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">전체</option>
                <option value="requested">대기 중</option>
                <option value="approved">처리 중</option>
                <option value="completed">완료</option>
                <option value="cancelled">취소</option>
              </select>
            </div>
          </div>
        </div>

        {/* 환불 목록 */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-500 border-r-transparent"></div>
              <p className="mt-4 text-slate-500">불러오는 중...</p>
            </div>
          ) : filteredRefunds.length === 0 ? (
            <div className="text-center py-12">
              <BanknotesIcon className="mx-auto h-12 w-12 text-slate-400" />
              <p className="mt-4 text-slate-500">환불 신청이 없습니다.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      신청 정보
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      수강생
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      환불 금액
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      상태
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      신청일
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">
                      작업
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredRefunds.map((refund) => (
                    <tr key={refund.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-slate-900">
                            {refund.program_info?.name || '프로그램 정보 없음'}
                          </p>
                          {refund.class_info?.name && refund.class_info.name !== '기본반' && (
                            <p className="text-sm text-slate-500">{refund.class_info.name}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">
                            {refund.application?.application_number}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-slate-900">
                            {refund.application?.applicant_name || refund.user_info?.name}
                          </p>
                          <p className="text-sm text-slate-500">
                            {refund.application?.applicant_phone || refund.user_info?.phone}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-semibold text-slate-900">
                          {refund.refund_amount?.toLocaleString()}원
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDisplayStatusColor(refund)}`}
                        >
                          {getDisplayStatusLabel(refund)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500">
                        {new Date(refund.requested_at).toLocaleDateString('ko-KR')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          {/* 상세 보기 */}
                          <button
                            onClick={() => openDetailModal(refund)}
                            className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                            title="상세 보기"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>

                          {/* 승인 버튼 (requested 상태에서만) */}
                          {refund.refund_status === 'requested' && (
                            <>
                              <button
                                onClick={() => handleApprove(refund.id)}
                                disabled={processingId === refund.id}
                                className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg disabled:opacity-50"
                                title="승인"
                              >
                                <CheckCircleIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => openRejectModal(refund)}
                                disabled={processingId === refund.id}
                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50"
                                title="거절"
                              >
                                <XCircleIcon className="h-5 w-5" />
                              </button>
                            </>
                          )}

                          {/* 완료 버튼 (approved 상태에서만) */}
                          {refund.refund_status === 'approved' && (
                            <button
                              onClick={() => handleComplete(refund.id)}
                              disabled={processingId === refund.id}
                              className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50"
                            >
                              환불 완료
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 상세 모달 */}
      {showDetailModal && selectedRefund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-slate-900">환불 상세 정보</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-slate-400 hover:text-slate-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* 환불 상태 */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="font-medium text-slate-700">환불 상태</span>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRefundStatusColor(
                    selectedRefund.refund_status
                  )}`}
                >
                  {getRefundStatusLabel(selectedRefund.refund_status)}
                </span>
              </div>

              {/* 신청 정보 */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">신청 정보</h4>
                <div className="space-y-2 text-sm">
                  <p>
                    <span className="text-slate-500">프로그램:</span>{' '}
                    <span className="text-slate-900">{selectedRefund.program_info?.name}</span>
                  </p>
                  {selectedRefund.class_info?.name && selectedRefund.class_info.name !== '기본반' && (
                    <p>
                      <span className="text-slate-500">반:</span>{' '}
                      <span className="text-slate-900">{selectedRefund.class_info.name}</span>
                    </p>
                  )}
                  <p>
                    <span className="text-slate-500">신청번호:</span>{' '}
                    <span className="text-slate-900">{selectedRefund.application?.application_number}</span>
                  </p>
                </div>
              </div>

              {/* 수강생 정보 */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">수강생 정보</h4>
                <div className="space-y-2 text-sm">
                  <p>
                    <span className="text-slate-500">이름:</span>{' '}
                    <span className="text-slate-900">
                      {selectedRefund.application?.applicant_name || selectedRefund.user_info?.name}
                    </span>
                  </p>
                  <p>
                    <span className="text-slate-500">연락처:</span>{' '}
                    <span className="text-slate-900">
                      {selectedRefund.application?.applicant_phone || selectedRefund.user_info?.phone}
                    </span>
                  </p>
                  {selectedRefund.user_info?.email && (
                    <p>
                      <span className="text-slate-500">이메일:</span>{' '}
                      <span className="text-slate-900">{selectedRefund.user_info.email}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* 환불 정보 */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">환불 정보</h4>
                <div className="space-y-2 text-sm">
                  <p>
                    <span className="text-slate-500">환불 금액:</span>{' '}
                    <span className="text-slate-900 font-semibold">
                      {selectedRefund.refund_amount?.toLocaleString()}원
                    </span>
                  </p>
                  <p>
                    <span className="text-slate-500">환불 사유:</span>{' '}
                    <span className="text-slate-900">{selectedRefund.refund_reason}</span>
                  </p>
                  <p>
                    <span className="text-slate-500">신청일:</span>{' '}
                    <span className="text-slate-900">
                      {new Date(selectedRefund.requested_at).toLocaleString('ko-KR')}
                    </span>
                  </p>
                  {selectedRefund.approved_at && (
                    <p>
                      <span className="text-slate-500">승인일:</span>{' '}
                      <span className="text-slate-900">
                        {new Date(selectedRefund.approved_at).toLocaleString('ko-KR')}
                      </span>
                    </p>
                  )}
                  {selectedRefund.completed_at && (
                    <p>
                      <span className="text-slate-500">완료일:</span>{' '}
                      <span className="text-slate-900">
                        {new Date(selectedRefund.completed_at).toLocaleString('ko-KR')}
                      </span>
                    </p>
                  )}
                </div>
              </div>

              {/* 환불 계좌 정보 */}
              {(selectedRefund.refund_bank || selectedRefund.refund_account) && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">환불 계좌</h4>
                  <div className="space-y-2 text-sm">
                    {selectedRefund.refund_bank && (
                      <p>
                        <span className="text-slate-500">은행:</span>{' '}
                        <span className="text-slate-900">{selectedRefund.refund_bank}</span>
                      </p>
                    )}
                    {selectedRefund.refund_account && (
                      <p>
                        <span className="text-slate-500">계좌번호:</span>{' '}
                        <span className="text-slate-900">{selectedRefund.refund_account}</span>
                      </p>
                    )}
                    {selectedRefund.refund_account_holder && (
                      <p>
                        <span className="text-slate-500">예금주:</span>{' '}
                        <span className="text-slate-900">{selectedRefund.refund_account_holder}</span>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* 거절 사유 */}
              {selectedRefund.rejection_reason && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-red-700 mb-1">거절 사유</h4>
                  <p className="text-sm text-red-600">{selectedRefund.rejection_reason}</p>
                </div>
              )}

              {/* 관리자 메모 */}
              {selectedRefund.admin_note && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-slate-700 mb-1">관리자 메모</h4>
                  <p className="text-sm text-slate-600">{selectedRefund.admin_note}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-4 border-t bg-slate-50">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                닫기
              </button>
              {selectedRefund.refund_status === 'requested' && (
                <>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      openRejectModal(selectedRefund);
                    }}
                    className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
                  >
                    거절
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      handleApprove(selectedRefund.id);
                    }}
                    disabled={processingId === selectedRefund.id}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    승인
                  </button>
                </>
              )}
              {selectedRefund.refund_status === 'approved' && (
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    handleComplete(selectedRefund.id);
                  }}
                  disabled={processingId === selectedRefund.id}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  환불 완료 처리
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 거절 모달 */}
      {showRejectModal && selectedRefund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-slate-900">환불 거절</h3>
              <button
                onClick={() => setShowRejectModal(false)}
                className="text-slate-400 hover:text-slate-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-900">
                  {selectedRefund.program_info?.name}
                </p>
                <p className="text-sm text-slate-500">
                  {selectedRefund.application?.applicant_name} /{' '}
                  {selectedRefund.refund_amount?.toLocaleString()}원
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  거절 사유 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  placeholder="환불 거절 사유를 입력해주세요"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t bg-slate-50">
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={processingId === selectedRefund.id}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                취소
              </button>
              <button
                onClick={handleReject}
                disabled={processingId === selectedRefund.id || !rejectionReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingId === selectedRefund.id ? '처리 중...' : '거절'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundManagement;
